import React, { useState, useEffect } from 'react';
import styles from './S_AddCapsuleContentPopup.module.css';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { M_FileProps } from '@/components/M_Molecules/M_File/M_File';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_Tag from '@/components/A_Atoms/A_Tag/A_Tag';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import W_AudioBar from '@/components/W_Wrappers/W_AudioBar/W_AudioBar';
import cn from 'classnames';
import { useTranslation } from '@/hooks/useTranslation';
import { useIsMobile } from '@/hooks/useIsMobile';

interface FileViewerProps {
  selectedFile: M_FileProps;
  currentDescription: string;
  onBackToUpload: () => void;
  onDescriptionChange: (description: string) => void;
  onDeleteDescription: () => void;
  onReplaceMedia: () => void;
  onSave: () => void;
}

export default function FileViewer({
  selectedFile,
  currentDescription,
  onBackToUpload,
  onDescriptionChange,
  onDeleteDescription,
  onReplaceMedia,
  onSave,
}: FileViewerProps) {
  const t = useTranslation();
  const isMobile = useIsMobile(1080);
  const [staticImageUrl, setStaticImageUrl] = useState<string>('');
  const [videoDuration, setVideoDuration] = useState<string>('');
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
  };

  const handleCloseVideo = () => {
    setIsVideoPlaying(false);
  };

  useEffect(() => {
    if (selectedFile.mediaType === 'video') {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        const duration = formatDuration(video.duration);
        setVideoDuration(duration);

        video.currentTime = 0.1;
        video.onseeked = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            const staticUrl = canvas.toDataURL('image/png');
            setStaticImageUrl(staticUrl);
          }
        };
      };

      video.onerror = e => {};

      video.oncanplay = () => {
        if (
          video.duration &&
          !isNaN(video.duration) &&
          isFinite(video.duration)
        ) {
          const duration = formatDuration(video.duration);
          setVideoDuration(duration);
        }
      };

      video.src = selectedFile.image;
    }
  }, [selectedFile.image, selectedFile.mediaType]);

  const renderMediaContent = () => {
    switch (selectedFile.mediaType) {
      case 'image':
        return (
          <img
            src={selectedFile.image}
            alt={t('capsuleCreate.contentUpload.filePreview')}
            className={styles.preview_image}
          />
        );

      case 'video':
        return isVideoPlaying ? (
          <div className={styles.content_wrapper}>
            <video
              src={selectedFile.image}
              autoPlay
              className={styles.preview_image}
            />
            <A_Tag
              label={videoDuration || t('capsuleCreate.contentUpload.video')}
              className={styles.timer_tag}
            />
            <div className={styles.video_button}>
              <A_CircleButton
                icon={<Q_Icon.CrossIcon width={24} height={24} fill='none' />}
                variant='glass'
                onClick={handleCloseVideo}
              />
            </div>
          </div>
        ) : (
          <div className={styles.content_wrapper}>
            <img
              src={staticImageUrl || selectedFile.image}
              alt={t('capsuleCreate.contentUpload.videoPreview')}
              className={styles.preview_image}
            />
            <A_Tag
              label={videoDuration || t('capsuleCreate.contentUpload.video')}
              className={styles.timer_tag}
            />
            <div className={styles.video_button}>
              <A_CircleButton
                icon={<Q_Icon.PlayIcon width={24} height={24} fill='none' />}
                variant='glass'
                onClick={handlePlayVideo}
              />
            </div>
          </div>
        );

      case 'audio':
        return <W_AudioBar audioUrl={selectedFile.image} />;

      case 'gif':
        return (
          <div className={styles.content_wrapper}>
            <img
              src={selectedFile.image}
              alt={t('capsuleCreate.contentUpload.gifPreview')}
              className={styles.preview_image}
            />
            <A_Tag
              label={t('capsuleCreate.contentUpload.gif')}
              className={styles.format_tag}
            />
          </div>
        );

      default:
        return (
          <img
            src={selectedFile.image}
            alt={t('capsuleCreate.contentUpload.filePreview')}
            className={styles.preview_image}
          />
        );
    }
  };
  return (
    <>
      <div className={styles.file_view_wrapper}>
        <div className={styles.breadcrumb}>
          <button
            className={cn(styles.breadcrumb_button, 'text-h-4')}
            onClick={onBackToUpload}
          >
            <Q_Icon.BackArrowIcon width={32} height={16} fill='none' />
            {t('capsuleCreate.contentUpload.backToUpload')}
          </button>
          <span className={cn(styles.breadcrumb_separator, 'text-h-4')}>/</span>
          <span className={cn(styles.breadcrumb_current, 'text-h-4')}>
            {t('capsuleCreate.contentUpload.viewMedia')}
          </span>
        </div>

        <div className={styles.file_preview_wrapper}>
          <div className={styles.file_preview}>{renderMediaContent()}</div>

          <A_Input
            label={t('capsuleCreate.contentUpload.description')}
            value={currentDescription}
            onChange={onDescriptionChange}
            maxLength={40}
            borderRadius='text-fields'
            maxWidth='100%'
          />
        </div>
      </div>

      <div
        className={cn(styles.buttons_wrapper, {
          [styles.mobile_buttons]: isMobile,
        })}
      >
        {isMobile ? (
          <>
            <div className={styles.buttons_row}>
              <A_Button
                variant='tertiary'
                size='small'
                fontSize='text-p-4'
                onClick={onDeleteDescription}
                disabled={!currentDescription}
                width='50%'
              >
                {t('capsuleCreate.contentUpload.deleteDescription')}
              </A_Button>
              <A_Button
                variant='tertiary'
                size='small'
                fontSize='text-p-4'
                onClick={onReplaceMedia}
                width='50%'
              >
                {t('capsuleCreate.contentUpload.replaceMedia')}
              </A_Button>
            </div>
            <A_Button
              variant='primary'
              size='small'
              fontSize='text-p-4'
              onClick={onSave}
              width='100%'
            >
              {t('capsuleCreate.contentUpload.save')}
            </A_Button>
          </>
        ) : (
          <>
            <A_Button
              variant='tertiary'
              size='small'
              fontSize='text-p-4'
              onClick={onDeleteDescription}
              disabled={!currentDescription}
            >
              {t('capsuleCreate.contentUpload.deleteDescription')}
            </A_Button>
            <div className={styles.buttons_wrapper_right}>
              <A_Button
                variant='tertiary'
                size='small'
                fontSize='text-p-4'
                onClick={onReplaceMedia}
              >
                {t('capsuleCreate.contentUpload.replaceMedia')}
              </A_Button>
              <A_Button
                variant='primary'
                size='small'
                fontSize='text-p-4'
                onClick={onSave}
              >
                {t('capsuleCreate.contentUpload.save')}
              </A_Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
